import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { requireProviderTags } from 'store/providers/providerSlice';

import messages from './Messages';
import ItemManagerModal from '../ItemManagerModal';
import ChartViewer from 'components/Chart/ChartViewer';
import Navigation from '../../../Navigation/Navigation';
import { getDomainAndPort } from '../../../../api/Utils';
import LoadingCircleIndicator from '../../../LoadingCircleIndicator/LoadingCircleIndicator';
import { getDateOf } from 'store/providers/providerTypes';
import APopover from '../../../APopover';
import Button from '../../../Button/Button';
import BaseDatePicker, { ILocal } from '../../../Chart/BaseDatePicker';
import { smartDateString } from '../../../../utils/date';
import Icon from '../../../Icon/Icon';
import Select from '../../../Select';
import { SurveyReportModalContainer, GlobalMarqueeStyle } from './styles';
import i18n, { getCalendarLocal, languageDirection } from '../../../../i18n';
import { smartNumericCompare } from '../../../../utils/string';
import {
  AnimationHandler,
  SelectTagContainer,
} from '../../../Navigation/styles';
import { withTitle } from '../../../withTitle';
import { CircleProgressBorder } from '../../../Progress/Progress';
import LocalIcon from '../../../../ui/LocalIcon';
import { exactWhite } from '../../../../utils/style/colors';

const domainInfo = getDomainAndPort();
class SurveyReportModal extends ItemManagerModal {
  state = {
    currentTab: 'List',
    baseDate: getDateOf(
      this.props.openItem.item.data.defaultEndTime || 'Today'
    ).toDate(),
    selectedTag: {},
    selectedCategorizedTag: '',
    isIntervalEnabled: false,
    windowSize: { width: window.innerWidth, height: window.innerHeight },
  };

  intervalRef = React.createRef(null);

  componentDidUpdate(prevProps, prevState) {
    super.componentDidUpdate();
    const uniqueTags = this.getUniqueTags();
    const shouldStartInterval =
      uniqueTags &&
      this.state.isIntervalEnabled &&
      this.state.currentTab === 'Dashboard';

    if (
      shouldStartInterval &&
      this.state.selectedCategorizedTag &&
      !this.intervalRef.current
    ) {
      this.intervalRef.current = setInterval(() => {
        this.handleNextPrevReport('next', false);
        clearInterval(this.intervalRef.current);
        this.intervalRef.current = null;
      }, 10 * 1000);
    }
  }

  componentDidMount() {
    super.componentDidMount();
    this.handleResize = () => {
      this.setState({
        windowSize: { width: window.innerWidth, height: window.innerHeight },
      });
    };
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  UNSAFE_componentWillMount() {
    this.props.dispatch(
      requireProviderTags({
        providerId: this.props.openItem.item.data.providerId,
      })
    );
    this.setState({
      currentTab: this.props.openItem.item.data.defaultViewType || 'List',
    });
  }

  setTab = (tab) => {
    this.setState({ currentTab: tab });
  };

  doNothing = (e) => {
    e.preventDefault();
  };

  handleSelectedCategorizedTag = (tag) => {
    this.setState({ selectedCategorizedTag: tag });
  };

  // action: prev | next
  handleNextPrevReport = (action, stopInterval = true) => {
    if (stopInterval) {
      if (this.intervalRef.current) {
        clearInterval(this.intervalRef.current);
        this.intervalRef.current = null;
      }
      this.setState({ isIntervalEnabled: false });
    }
    const uniqueTags = this.getUniqueTags();
    if (uniqueTags) {
      const tagIndex = this.state.selectedCategorizedTag
        ? uniqueTags.indexOf(this.state.selectedCategorizedTag)
        : 0;
      let targetIndex = null;
      if (action === 'prev') {
        targetIndex = tagIndex - 1;
        targetIndex =
          targetIndex >= 0 ? targetIndex : uniqueTags.length + targetIndex;
      } else {
        targetIndex = tagIndex + 1;
        targetIndex = targetIndex % uniqueTags.length;
      }
      this.handleSelectedCategorizedTag(uniqueTags[targetIndex]);
    }
  };

  getUniqueTags = () => {
    const { openItem, surveys } = this.props;
    const reportIds = [
      ...(openItem.item.data.surveyReportLinks || []).reduce((acc, report) => {
        acc.push(report.reportId);
        return acc;
      }, []),
      ...(Array.isArray(openItem.item.data.reportIds)
        ? openItem.item.data.reportIds
        : []),
    ];

    const reports = Object.values(surveys.reports.data).filter((report) =>
      reportIds.includes(report.id)
    );

    const uniqueTags = [
      ...reports
        .reduce((acc, report) => {
          if (report.tags?.length)
            return Array.from(new Set([...acc, ...report.tags]));
          else {
            return Array.from(
              new Set([...acc, i18n.t('others', { ns: 'modals' })])
            );
          }
        }, [])
        .sort(smartNumericCompare),
    ];

    return uniqueTags;
  };

  renderChild() {
    const {
      openItem,
      onMinimize,
      onClose,
      provider,
      showAnimation = false,
      surveys,
      updatedProviderItem,
    } = this.props;
    const local = this.props.openItem.providerId
      ? this.props.providers.map[this.props.openItem.providerId].data
          ?.surveyOwnerProperties.localId || getCalendarLocal()
      : getCalendarLocal();

    if (!provider) return <LoadingCircleIndicator />;
    const { currentTab, baseDate, selectedTag } = this.state;
    let finalCurrentTab = currentTab;
    let SURVEY_ITEMS = [
      {
        id: 'List',
        label: i18n.t('list', { ns: 'taskManager' }),
        order: 1,
      },
      {
        id: 'Fullscreen',
        label: i18n.t('fullscreen', { ns: 'taskManager' }),
        order: 2,
      },
      {
        id: 'BSC',
        label: i18n.t('BSC', { ns: 'taskManager' }),
        order: 4,
      },
    ];
    SURVEY_ITEMS.push({
      id: 'Dashboard',
      label: i18n.t('dashboard', { ns: 'taskManager' }),
      order: 3,
    });
    if (domainInfo.view === 'publicSingle' || domainInfo.view === 'public') {
      SURVEY_ITEMS = [
        {
          id: 'Fullscreen',
          label: i18n.t('fullscreen', { ns: 'taskManager' }),
          order: 2,
        },
      ];
      finalCurrentTab = 'Fullscreen';
    }
    SURVEY_ITEMS = SURVEY_ITEMS.sort((a, b) => b.order - a.order);
    const tagOptions = openItem.item.data.filterOptions?.map(
      (filterOption) =>
        Object.values(this.props?.tags || {}).reduce((current, item) => {
          if (
            filterOption?.filterSelectTagRoots?.includes(item.parentId) &&
            !item.deleted
          )
            current.push({
              value: item.id,
              label: item.text,
            });
          return current;
        }, []) || []
    );
    const selectedTagOption = openItem.item.data.filterOptions?.map(
      (_, index) =>
        selectedTag[index]
          ? [
              {
                label: this.props.tags[this.state.selectedTag[index]]?.text,
                value: this.props.tags[this.state.selectedTag[index]]?.id,
              },
            ]
          : []
    );

    let customButton = (
      <>
        {finalCurrentTab !== 'List' && (
          <APopover
            arrow
            curveBorder
            trigger="click"
            target={
              <Button
                style={{ marginInlineStart: '10px', borderRadius: '25px;' }}
                size="small"
              >
                <Icon type="calendar" style={{ marginInlineEnd: '5px' }} />
                {i18n.t('until', { ns: 'taskManager' }) +
                  smartDateString(this.state.baseDate, false, true, false)}
              </Button>
            }
          >
            <div style={{ backgroundColor: exactWhite, maxWidth: '350px' }}>
              <BaseDatePicker
                baseDate={this.state.baseDate}
                onChangeBaseDate={(baseDate) => this.setState({ baseDate })}
                sendDateOnTabChange={false}
                showDaySelect={true}
                local={local}
              />
            </div>
          </APopover>
        )}
        {this.props.tags &&
          (this.state.windowSize.width > 600 ? (
            openItem.item.data.filterOptions.map((filterOption, index) => (
              <span
                key={index}
                style={{ marginInlineStart: '1rem', fontSize: '0.8rem' }}
              >
                <Select
                  onChangeValues={(selected) => {
                    const n = { ...selectedTag };
                    n[index] = selected.length ? selected[0].value : undefined;
                    this.setState({
                      selectedTag: n,
                    });
                  }}
                  textInputStyle={{ fontSize: '0.8rem ', height: '1rem' }}
                  options={tagOptions[index]}
                  values={selectedTagOption[index]}
                  placeholder={
                    filterOption.filterTitle ||
                    i18n.t('filterReport', { ns: 'taskManager' })
                  }
                  round={true}
                />
              </span>
            ))
          ) : (
            <APopover
              arrow
              curveBorder
              overflowVisible
              trigger="click"
              target={
                <Button
                  style={{ marginInlineStart: '10px', borderRadius: '5px' }}
                  size="small"
                  color="black-outline"
                >
                  <div
                    style={{
                      maxWidth: '200px',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <div
                      style={{
                        display: 'inline-block',
                        animation: Object.values(selectedTag).some((tag) => tag)
                          ? 'marquee 10s linear infinite'
                          : 'none',
                        maxWidth: '8rem',
                      }}
                    >
                      {Object.values(selectedTag).some((tag) => tag)
                        ? openItem.item.data.filterOptions
                            .map((filterOption, index) =>
                              selectedTag[index]
                                ? `${filterOption.filterTitle || 'Filter'}: ${this.props.tags[selectedTag[index]]?.text}`
                                : ''
                            )
                            .filter(Boolean)
                            .join(' | ') ||
                          i18n.t('reportFilters', { ns: 'taskManager' })
                        : i18n.t('reportFilters', { ns: 'taskManager' })}
                    </div>
                  </div>
                </Button>
              }
            >
              <div
                style={{
                  backgroundColor: 'var(--exact-white)',
                  padding: '10px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                {openItem.item.data.filterOptions.map((filterOption, index) => (
                  <Select
                    key={index}
                    inPopover
                    onChangeValues={(selected) => {
                      const n = { ...selectedTag };
                      n[index] = selected.length
                        ? selected[0].value
                        : undefined;
                      this.setState({
                        selectedTag: n,
                      });
                    }}
                    options={tagOptions[index]}
                    values={selectedTagOption[index]}
                    placeholder={
                      filterOption.filterTitle ||
                      i18n.t('filterReport', { ns: 'taskManager' })
                    }
                    label={
                      filterOption.filterTitle ||
                      i18n.t('filterReport', { ns: 'taskManager' })
                    }
                    round={true}
                  />
                ))}
              </div>
            </APopover>
          ))}
      </>
    );

    const uniqueTags = this.getUniqueTags();

    const tagPaginationOptions = uniqueTags
      ? uniqueTags.map((tagName) => ({ label: tagName, value: tagName }))
      : [];

    const isRtl = languageDirection() === 'rtl';

    const PaginatedReportHandler = (
      <div
        style={{
          flex: '1',
          height: '3rem',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {this.state.currentTab === 'Dashboard' &&
          tagPaginationOptions.length > 1 && (
            <>
              <SelectTagContainer>
                <Select
                  placeholder={i18n.t('page', { ns: 'modals' })}
                  options={tagPaginationOptions}
                  values={tagPaginationOptions.filter(
                    (tag) => tag.label === this.state.selectedCategorizedTag
                  )}
                  onChangeValues={(values) => {
                    this.setState({ isIntervalEnabled: false });
                    this.handleSelectedCategorizedTag(values?.[0]?.label || '');
                  }}
                  maxWidth={'200px'}
                  style={{ width: '200px' }}
                  multiChoice={false}
                />
              </SelectTagContainer>

              <AnimationHandler>
                {withTitle(
                  <Icon
                    type={isRtl ? 'chevron-right' : 'chevrons-left'}
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      this.handleNextPrevReport(isRtl ? 'next' : 'prev')
                    }
                  />,
                  i18n.t(isRtl ? 'next' : 'prev', { ns: 'modals' })
                )}

                <CircleProgressBorder
                  duration={10}
                  autoPlay={this.state.isIntervalEnabled}
                  strokeColor={'white'}
                >
                  <LocalIcon
                    iconName={
                      this.state.isIntervalEnabled ? 'pause.svg' : 'play.svg'
                    }
                    type={'xs'}
                    color={exactWhite}
                    isLink={true}
                    onClick={() => {
                      this.setState((prevState) => {
                        if (
                          !prevState.isIntervalEnabled &&
                          !this.state.selectedCategorizedTag &&
                          uniqueTags
                        ) {
                          this.handleSelectedCategorizedTag(uniqueTags[0]);
                        }
                        return {
                          ...prevState,
                          isIntervalEnabled: !prevState.isIntervalEnabled,
                        };
                      });
                    }}
                  />
                </CircleProgressBorder>

                {withTitle(
                  <Icon
                    type={isRtl ? 'chevron-left' : 'chevron-right'}
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      this.handleNextPrevReport(isRtl ? 'prev' : 'next')
                    }
                  />,
                  i18n.t(isRtl ? 'prev' : 'next', { ns: 'modals' })
                )}
              </AnimationHandler>
            </>
          )}
      </div>
    );

    return (
      <>
        <GlobalMarqueeStyle />
        <SurveyReportModalContainer
          isOpen={this.props.isOpen}
          showAnimation={showAnimation}
        >
          <Navigation
            items={SURVEY_ITEMS}
            onClose={onClose}
            onMinimize={onMinimize}
            onSelect={this.setTab}
            defaultTab={finalCurrentTab}
            showItems={SURVEY_ITEMS.length > 1}
            title={openItem.item.data.name}
            customDropDown={customButton}
            PaginatedReportHandler={PaginatedReportHandler}
            uniqueTags={uniqueTags}
            selectedCategorizedTag={this.state.selectedCategorizedTag}
            onSetSelectedCategorizedTag={this.handleSelectedCategorizedTag}
          />
          <ChartViewer
            reportItem={
              updatedProviderItem
                ? { ...openItem, item: updatedProviderItem }
                : openItem
            }
            viewType={finalCurrentTab}
            baseDate={baseDate}
            onChangeBaseDate={(baseDate) => this.setState({ baseDate })}
            externalTagFilter={Object.values(selectedTag).filter((it) => !!it)}
            extraTagIds={(
              updatedProviderItem?.data || openItem.item.data
            ).forceFilter
              ?.split(',')
              .filter((it) => it.startsWith('T'))
              .map((it) => Number(it.substring(1)))}
            selectedCategorizedTag={this.state.selectedCategorizedTag}
          />
        </SurveyReportModalContainer>
      </>
    );
  }
}

SurveyReportModal.propTypes = {
  openItem: PropTypes.shape({
    item: PropTypes.object.isRequired,
    status: PropTypes.oneOf(['edit', 'view']),
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onMinimize: PropTypes.func,
  showAnimation: PropTypes.bool,
};

const mapStateToProps = (state, props) => {
  const tags =
    state.providers?.tags?.[props.openItem.item.data.providerId]?.data;
  const provider =
    state.providers.map[props.openItem.item.data.providerId].data;
  const surveys = state.survey;
  const providerId = props.openItem.item.data.providerId;
  const itemId = props.openItem.item.data.id;
  const updatedProviderItem =
    state.providers.items[providerId]?.items?.[itemId];
  return { tags, provider, surveys, updatedProviderItem };
};

export default connect(mapStateToProps)(SurveyReportModal);
